import { Box, Divider, Flex, ListItem, Text, TextButton, Tooltip } from '@modusign/design-system';

import useParticipantSelect from '../../hooks/useParticipantSelect';
import getFormattedParticipantInfo from '../../lib/getFormattedParticiapntInfo';
import { InPersonDetail } from '../../types';
import HostSectionalBanner from '../HostSectionalBanner';
import InPersonInfoCardLayout from '../InPersonInfoCardLayout';
import { ResponsiveTable, ResponsiveTableCell } from '../ResponsiveTable';
import SignerListCell from '../SignerListCell';

interface ParticipantSelectProps {
  isLoading?: boolean;
  currentHostDataLabel: string;
  inPersonDetail: InPersonDetail;
  onStartInPersonSigningClick: (selectedParticipantId?: string) => void;
  onGoToCancelSigningClick: () => void;
  onGoToRejectSigningClick: () => void;
}

export default function ParticipantSelect({
  isLoading,
  currentHostDataLabel,
  inPersonDetail,
  onStartInPersonSigningClick,
  onGoToCancelSigningClick,
  onGoToRejectSigningClick,
}: ParticipantSelectProps) {
  const { name, currentSigningOrder, hosts } = inPersonDetail;
  const currentHost = hosts.find(({ dataLabel }) => dataLabel === currentHostDataLabel);
  const {
    currentSignersWithSigningStatus,
    canStartInPersonSession,
    selectedSigner,
    setSelectedSignerIfValid,
  } = useParticipantSelect({ currentHost, inPersonDetail });

  const canCancelSign = currentSignersWithSigningStatus.some((p) => p.hasSigned);
  const canRejectSign = currentSignersWithSigningStatus.some(
    (p) => !p.hasSigned && p.signingExpiryDate.isValid
  );

  const disabledMessage = canStartInPersonSession
    ? null
    : '대면서명을 진행할 수 있는 서명자가 없습니다.';

  const documentData = [
    {
      title: '문서 제목',
      value: (
        <ResponsiveTableCell>
          <Text>{name}</Text>
        </ResponsiveTableCell>
      ),
    },
    {
      title: '진행자',
      value: (
        <ResponsiveTableCell>
          <Text>
            {getFormattedParticipantInfo(currentHost?.name || '', currentHost?.contactInfo || '')}
          </Text>
        </ResponsiveTableCell>
      ),
    },
    {
      title: '서명자',
      value: (
        <SignerListCell
          currentSigningOrder={currentSigningOrder}
          selectedSigner={selectedSigner}
          signers={currentSignersWithSigningStatus}
          onSelectParticipant={setSelectedSignerIfValid}
        />
      ),
    },
  ];

  return (
    <>
      <Box mb="4">
        <HostSectionalBanner hostName={currentHost?.name || ''} />
      </Box>
      <InPersonInfoCardLayout
        buttonText="대면서명 진행하기"
        description="아래 서명자의 대면서명을 진행합니다. 주의사항을 확인 후 진행해 주세요."
        disabledMessage={disabledMessage}
        isLoading={isLoading}
        title="확인 및 동의"
        onFooterButtonClicked={() => onStartInPersonSigningClick(selectedSigner?.participantId)}
      >
        <ResponsiveTable dataList={documentData} />
        <Box
          as="ul"
          backgroundColor="boxBackgroundNeutralBase"
          borderColor="boxBorderNeutralBase"
          borderStyle="solid"
          borderWidth="1px"
          mt={4}
          px={6}
          py={7}
          width="100%"
        >
          <ListItem mb={2}>
            <Text fontSize="sm">위 서명자의 신원을 확인 후 서명 진행해 주세요.</Text>
          </ListItem>
          <ListItem>
            <Text fontSize="sm">
              서명자가 서명 완료 후, 완료된 문서의 사본을 잘 받았는지 확인해 주세요.
            </Text>
          </ListItem>
        </Box>
      </InPersonInfoCardLayout>
      <Flex alignItems="center" flexDirection="column" justifyContent="center" m={6} mb={11}>
        <Text color="textNeutralSlightlyLight" fontSize="sm" fontWeight="regular">
          서명자가 서명 거절/취소를 요청했나요?
        </Text>
        <Flex alignItems="center" justifyContent="center" my={1}>
          <ToolTipTextButton
            buttonText="서명 거절"
            disabled={!canRejectSign}
            toolTipMessage="서명을 거절할 수 있는 서명자가 없습니다."
            onClick={onGoToRejectSigningClick}
          />
          <Divider
            direction="column"
            mx={3}
            sx={{
              height: '8px',
            }}
          />
          <ToolTipTextButton
            buttonText="서명 취소"
            disabled={!canCancelSign}
            toolTipMessage="서명을 취소할 수 있는 서명자가 없습니다."
            onClick={onGoToCancelSigningClick}
          />
        </Flex>
      </Flex>
    </>
  );
}

function ToolTipTextButton({
  toolTipMessage,
  buttonText,
  disabled,
  onClick,
}: {
  toolTipMessage: string;
  buttonText: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip
      content={
        <Text fontSize="xs" fontWeight="medium">
          {toolTipMessage}
        </Text>
      }
      disabled={!disabled}
      placement="bottom"
    >
      <Box>
        <TextButton disabled={disabled} fontSize="sm" fontWeight="regular" onClick={onClick}>
          {buttonText}
        </TextButton>
      </Box>
    </Tooltip>
  );
}
